import { memo, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { useDialogController } from '@machobear/design-system'

import { breakpoints, colors } from '@/styles'
import { useFilters } from '@/hooks/use-filters'
import useSessionComplete from '@/hooks/use-session-complete'
import useSetTrrebDlv from '@/hooks/use-set-trreb-dlv'
import { BuildingListModal } from '@/features/Listings/map/building-list-modal'
import { BuildingCard } from '@/features/Listings/map/cards'
import ListingCard from '@/components/listing-card'
import LockedListingCard from '@/components/listing-card/locked-listing-card'
import { useTermsDialogContext } from '@/components/terms-dialog/context'
import { Label } from '@/components/text'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${colors.grey48};
`

const VirtualRowGrid = styled.div`
  display: grid;
  grid-gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));

  @media (max-width: ${breakpoints.map}) {
    grid-template-columns: repeat(auto-fill, minmax(100%, 1fr));
  }
`

const VirtualListContainer = styled.div`
  width: 100%;
  position: relative;
`

const VirtualItem = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  box-sizing: border-box;
`

const RegularListings = memo(
  ({
    loading,
    buildings = [],
    setHighlightedBuilding,
    setVisitedBuilding,
    hideFeaturedListings,
    onlyVerifiedListings,
    virtualRows,
    totalSize,
    columns,
    isMobile
  }) => {
    const { t } = useTranslation()
    const router = useRouter()
    const hasSession = useSessionComplete()
    const [filters] = useFilters()
    const { visible, showModal, closeModal } = useDialogController()
    const [buildingData, setBuildingData] = useState(null)

    const { isLoading, checkAgreementMismatch } = useTermsDialogContext()
    const isAgreementMismatched = checkAgreementMismatch('trreb_agreements')
    const isAgreementLoading = isLoading && hasSession

    useSetTrrebDlv(isAgreementMismatched)

    const buildingFilters = onlyVerifiedListings
      ? { ...filters, search_only_verified: true }
      : filters

    const titleText = t('b.listings.view.most_relevant.text', 'Most Relevant')
    const showSkeletonState =
      buildings.length === 0 || (hasSession && isAgreementLoading)

    const renderItem = (building, index) => {
      if (!building) return null
      const { building_id: buildingId, listing_count, listings } = building
      const isBuilding = listing_count > 1
      const isImported = listings && listings[0]?.public_viewing === false
      const isCardLocked =
        isImported &&
        (isAgreementMismatched || !hasSession || isAgreementLoading)

      return isBuilding ? (
        <BuildingCard
          index={index}
          key={buildingId}
          building={building}
          onMouseEnter={() => setHighlightedBuilding(buildingId)}
          onMouseLeave={() => setHighlightedBuilding('')}
          onClick={() => {
            setBuildingData(building)
            setVisitedBuilding(buildingId)
            showModal()
          }}
          loading={loading}
          trackImpression={{
            enabled: true,
            page: 'listings',
            section: 'building-card',
            sponsored: false
          }}
          onComplete={() => closeModal()}
        />
      ) : isCardLocked ? (
        <LockedListingCard
          listing={{ ...(listings ? listings[0] : {}), ...building }}
          index={index}
          key={buildingId}
          noSchema
          favouriteButton
          loading={loading}
        />
      ) : (
        <ListingCard
          listing={{ ...(listings ? listings[0] : {}), ...building }}
          key={buildingId}
          index={index + 1}
          favouriteButton
          noSchema
          newTab
          loading={showSkeletonState}
          onMouseEnter={() => setHighlightedBuilding(buildingId)}
          onMouseLeave={() => setHighlightedBuilding('')}
          trackImpression={{
            enabled: true,
            page: 'listings',
            section: 'listing-card',
            sponsored: false
          }}
          onClick={() => setVisitedBuilding(buildingId)}
        />
      )
    }

    return (
      <Wrapper>
        {!hideFeaturedListings && <Label fontSize="14px">{titleText}</Label>}
        {showSkeletonState ? (
          <VirtualRowGrid aria-label="listings-grid-skeleton">
            <ListingCard skeleton />
            <ListingCard skeleton />
            <ListingCard skeleton />
            <ListingCard skeleton />
            <ListingCard skeleton />
            <ListingCard skeleton />
          </VirtualRowGrid>
        ) : isMobile ? (
          <VirtualRowGrid>
            {buildings.map((building, index) => renderItem(building, index))}
          </VirtualRowGrid>
        ) : (
          <VirtualListContainer style={{ height: `${totalSize}px` }}>
            {virtualRows.map(virtualRow => {
              const firstItemIndex = virtualRow.index * columns
              const items = buildings
                .slice(firstItemIndex, firstItemIndex + columns)
                .map((building, itemIndex) => {
                  const index = firstItemIndex + itemIndex
                  return renderItem(building, index)
                })

              return (
                <VirtualItem
                  key={virtualRow.key}
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <VirtualRowGrid>{items}</VirtualRowGrid>
                </VirtualItem>
              )
            })}
          </VirtualListContainer>
        )}
        <BuildingListModal
          id={buildingData?.building_name}
          visible={visible}
          onClose={closeModal}
          building_id={buildingData?.building_id}
          building={buildingData}
          filters={buildingFilters}
          router={router}
        />
      </Wrapper>
    )
  }
)

RegularListings.displayName = 'RegularListings'

export default RegularListings
